/* eslint-disable max-lines -- should be fixed when removing objectless code */
import { inject, Injectable, InjectionToken } from '@angular/core';
import { Store } from '@sama/app-state';
import { assert, isDefined, isTruthy, mapToObject } from '@sama/js-core';
import {
  forOwn,
  isEmpty,
  keys,
  mapValues,
  matches,
  values,
} from '@sama/micro-dash';
import { first } from 'lodash';
import { combineLatest, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { RollbarService } from '../../../cross-team-shared/messaging/rollbar.service';
import { getInputUrls } from '../../../cross-team-shared/misc-utils';
import { assertStore } from '../../../cross-team-shared/misc-utils/assert-store';
import {
  isPointCloudOutput,
  isSensorFusionOutput,
  isVideoOutput,
} from '../../../cross-team-shared/misc-utils/output-utils';
import { OutputInfoState } from '../../../outputs/annotation/annotation-shared/annotation-providers';
import { WorkspaceConfigService } from '../../../shared/misc-services/workspace-config.service';
import { getOutputHelper } from '../../../shared/output-helpers/get-output-helper';
import { AnnotationOutput } from '../../../team-apis/otterdam/outputs/annotation-output';
import { AbstractOutput } from '../../../team-apis/otterdam/outputs/output';
import { Project } from '../../../team-apis/otterdam/project';
import { Task } from '../../../team-apis/otterdam/task';
import { getShapeGroupOrderFromTaskObjectless } from '../../outputs/annotation/annotation-shared/shape-layer-answer-helper';
import { createPlaceholderShapes } from '../../shared/misc-services/objectless-shape.service';
import {
  getFusionOutput,
  getOutputByName,
  isAnnotationOutput,
} from '../dsl/sensor-fusion/sensor-fusion-output.utils';
import { FusionState } from './fusion-state';
import { H3mlStore } from './h3ml-store';
import { OutputInfo, OutputInfoByName } from './output-info';
import { TaskWorkspaceState } from './task-workspace-state';
import { buildTrainingShapesObjectless } from './task-workspace-state.utils';

export const fusionStoreToken = new InjectionToken<Store<FusionState>>(
  'fusion store',
);

@Injectable()
export class TaskWorkspaceStore extends H3mlStore<TaskWorkspaceState> {
  private readonly workspaceConfigService = inject(WorkspaceConfigService);

  compareAnswerChanged$ = new Subject<void>();

  constructor(private rollbarService: RollbarService) {
    super(new TaskWorkspaceState());

    this('outputInfoByName')
      .$.pipe(filter(isDefined))
      .subscribe(this.updateRollbarPayload.bind(this));
  }

  setProject(project: Project) {
    this.batch(() => {
      const outputInfoByName = OutputInfo.createBlanksByName(
        project.outputs,
        project.use_annotation_store,
      );
      this.assign({
        project,
        outputInfoByName,
        outputAnnotationInfo: this.getOutputAnnotationInfo(project),
      });
      if (!project.use_annotation_store) {
        this.resetOutputInfo(this.state().task, outputInfoByName);
        this.initializeShapesObjectless(this.state().task);
      }
    });
    this.setStepLayout();
    this.overrideNestedOutputColorId(project);
  }

  setTask(task: Task) {
    this.batch(() => {
      this('task').set(task);
      this.resetOutputInfoAndExtras(task, this.state().outputInfoByName);
    });
  }

  getInputUrl(inputName: string) {
    return this.getInputUrls(inputName)[0];
  }

  getInputUrls(inputName: string): string[] {
    const { task } = this.state();
    return getInputUrls(task, inputName, task._extras.signed_urls);
  }

  getLoadedFramesStore(outputName: string) {
    // @ts-ignore -- fix me
    return this('outputAnnotationInfo')(outputName)('sensorRendererState')(
      'loadedFrames',
    );
  }

  getMaxFrameNumber$(outputName: string) {
    return this.getLoadedFramesStore(outputName).$.pipe(
      filter(isDefined),
      // @ts-ignore -- fix me
      map((frames) => frames.length - 1),
    );
  }

  getMaxFrameNumber(outputName: string) {
    const loadedFramesStore = this.getLoadedFramesStore(outputName).state();
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (!loadedFramesStore) {
      return 0;
    }
    // @ts-ignore -- fix me
    return loadedFramesStore.length - 1;
  }

  getFirstFusion() {
    const firstFusion = first(values(this.state().fusions));
    assert(firstFusion, 'there must be one and only one fusion store');
    return firstFusion;
  }

  claimNextInternalId() {
    const idStore = this.getNextShapeIdStore();
    const nextId = idStore.state();
    idStore.set(nextId + 1);
    return nextId;
  }

  getStep() {
    const steps = this.state().project.steps;
    return steps[steps.findIndex(matches({ id: this.state().stepId }))];
  }

  resetOutputInfoAndExtras(task: Task, outputInfoByName: OutputInfoByName) {
    this('outputTrainingScore').set({});
    if (!this.isUsingObjects()) {
      this.resetOutputInfo(task, outputInfoByName);
      this.initializeShapesObjectless(task);
    }
  }

  shapeHasFeedback$(shapeId: number) {
    return this.getShapeFeedback$(shapeId).pipe(
      map((shapeFeedback) => !isEmpty(shapeFeedback)),
    );
  }

  getShapeFeedback$(shapeId: number) {
    return this('shapeFeedback').$.pipe(
      map((feedbackList) => feedbackList.filter(matches({ shapeId }))),
    );
  }

  isThumbnail$(outputName: string) {
    return combineLatest([
      this('selectedOutputName').$,
      this('splitViewOutputName').$,
    ]).pipe(map(() => this.isInThumbnailLayout(outputName)));
  }

  getLastSubmission() {
    const submissions = this.state().task.submissions;
    assert(submissions, 'Submission should be present.');

    return submissions[submissions.length - 1];
  }

  getSelectedOutput() {
    return getOutputByName(
      this.state().project,
      this.state().selectedOutputName,
    );
  }

  getShapeStore(outputName: string, index: number) {
    const shapeStore = this.getShapesStore(outputName)(index);
    assertStore(shapeStore, `shape store for index ${index} not found`);
    return shapeStore;
  }

  getShapesStore(outputName: string) {
    const outputStore = this('outputAnnotationInfo')(outputName);
    assertStore(outputStore, 'output store not found');
    return outputStore('shapes');
  }

  getObjectStore(objectId: number) {
    const objectStore = this('objects')(objectId);
    assertStore(objectStore, `object store for id ${objectId} not found`);
    return objectStore;
  }

  isUsingObjects() {
    return isTruthy(this.state().project.use_annotation_store);
  }

  isUsingGenAi() {
    return this.state().taxonomy.type === 'genai';
  }

  isUsingCategorization() {
    return this.state().taxonomy.type === 'categorization';
  }

  isUsingGenAi$() {
    return this('taxonomy')('type').$.pipe(map(() => this.isUsingGenAi()));
  }

  isUsingCategorization$() {
    return this('taxonomy')('type').$.pipe(
      map(() => this.isUsingCategorization()),
    );
  }

  hasTaxonomy() {
    return !isEmpty(this.state().taxonomy.objectTaxonomy);
  }

  getFirstAvailableVideoOutput() {
    return this.state().outputsByDisplayOrder.filter(isVideoOutput)[0]?.name;
  }

  private isInThumbnailLayout(outputName: string) {
    const { project, selectedOutputName, splitViewOutputName } = this.state();

    if (splitViewOutputName) {
      return false;
    }

    const output = getOutputByName(project, outputName);

    // Outputs not in a fusion should never be in thumbnail mode
    const fusionOutput = getFusionOutput(project, output.id);
    if (!fusionOutput || fusionOutput.meta.fused_outputs.length === 1) {
      return false;
    }

    return outputName !== selectedOutputName;
  }

  private resetOutputInfo(task: Task, outputInfosToSet: OutputInfoByName) {
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (!(task && outputInfosToSet)) {
      return;
    }
    this.setOutputInfoObjectless(task, outputInfosToSet);
  }

  private getOutputAnnotationInfo(
    project: Project,
  ): StrictRecord<AbstractOutput['name'], OutputInfoState> {
    return mapToObject(project.outputs.filter(isAnnotationOutput), (output) => [
      output.name,
      {} as OutputInfoState,
    ]);
  }

  /** @deprecated used for old projects without objects */
  private setOutputInfoObjectless(
    task: Task,
    outputInfosToSet: OutputInfoByName,
  ) {
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (!(task && outputInfosToSet)) {
      return;
    }
    this('outputInfoByName').setUsing((outputInfoByName) =>
      mapValues(outputInfosToSet, (outputInfo, outputName) => {
        const answer = task.answers_or_gold[outputName];
        const outputHelper = getOutputHelper(outputInfo.output.type);
        return {
          ...outputInfoByName[outputName],
          // Each output is responsible for setting these back to
          // true after it is done initializing/loading assets/etc.
          canSaveDrafts: false,
          initialized: false,
          answer: outputHelper.isAnswerBlank(answer)
            ? outputHelper.getBlankAnswer(outputInfo.output)
            : answer,
        };
      }),
    );
  }

  // overrides the output's nested_output_color_id with the fusion output's
  // value if the fusion output has answer options (fused labels) and one
  // of those options has been selected as the nested output for shape color
  private overrideNestedOutputColorId(project: Project) {
    forOwn(this.state().outputInfoByName, (outputInfo, outputName) => {
      if (!isAnnotationOutput(outputInfo.output)) {
        return;
      }

      const fusionOutput = getFusionOutput(project, outputInfo.output.id);
      let nestedOutputColorId = outputInfo.output.meta.nested_output_color_id;
      if (
        fusionOutput &&
        // fix me if you change this code
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
        !isEmpty(fusionOutput?.answer_options) &&
        !!fusionOutput.meta.nested_output_color_id
      ) {
        nestedOutputColorId = fusionOutput.meta.nested_output_color_id;
      }

      const outputInfoStore: Store<OutputInfo<AnnotationOutput>> =
        this('outputInfoByName')(outputName);
      outputInfoStore('output')('meta')('nested_output_color_id').set(
        nestedOutputColorId,
      );
    });
  }

  private updateRollbarPayload() {
    const state = this.state();
    this.rollbarService.configure({
      payload: {
        // fix me if you change this code
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
        taskId: state.task && state.task.id,
        // fix me if you change this code
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
        projectId: state.project && state.project.id,
        answers: this.extractAnswersFromOutputInfo(),
      },
    });
  }

  private initializeShapesObjectless(task: Task | undefined) {
    const project = this.state().project;
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (!project || !task) {
      return;
    }

    const annotationOutputs = project.outputs.filter(isAnnotationOutput);
    const trainingAnswers = task._extras.training?.answers;

    this.batch(() => {
      annotationOutputs.forEach((output) => {
        const trainingAnswersForOutput = trainingAnswers?.[output.name];

        this('outputAnnotationInfo')(output.name).assign({
          ...getShapeGroupOrderFromTaskObjectless(task, output),
          trainingShapes: buildTrainingShapesObjectless(
            trainingAnswers,
            trainingAnswersForOutput,
            output,
          ),
        });
      });
    });
    this.createFusionPlaceholders();
  }

  /** @deprecated used for old projects without objects */
  private createFusionPlaceholders() {
    keys(this.state().outputAnnotationInfo).forEach((outputName) => {
      const output = getOutputByName(this.state().project, outputName);
      if (isPointCloudOutput(output)) {
        const shapes = this.getShapesStore(outputName).state();
        values(shapes).forEach((shape) => {
          if (shape) {
            createPlaceholderShapes(this, output, shape);
          }
        });
      }
    });
  }

  private getNextShapeIdStore() {
    return this.isUsingObjects()
      ? this('nextShapeId')
      : this('task')('next_internal_id');
  }

  private setStepLayout() {
    const stepId = this.workspaceConfigService.stepId;
    const step = this.state().project.steps.find(matches({ id: stepId }));

    // step is not defined for all workspace types here
    if (step) {
      this('layout').set(step.layout);
    }
  }
}

export const fusionStoreProvider = {
  provide: fusionStoreToken,
  deps: [TaskWorkspaceStore],
  useFactory: (taskWorkspaceStore: TaskWorkspaceStore) => {
    const { project } = taskWorkspaceStore.state();
    const fusionOutput = project.outputs.find(isSensorFusionOutput);
    assert(
      isDefined(fusionOutput),
      'object projects should always have a fusion output',
    );

    return taskWorkspaceStore('fusions')(fusionOutput.id);
  },
};
